export type LegalLanguage = "zh" | "en";
export type LegalPath =
  | "/privacy"
  | "/terms"
  | "/refund"
  | "/acceptable-use"
  | "/ai-disclosure"
  | "/cookies";

type LegalSection = {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
};

export type LegalDocument = {
  eyebrow: string;
  title: string;
  updated: string;
  summary: string;
  sections: readonly LegalSection[];
};

const zhPrivacy: LegalDocument = {
  eyebrow: "隐私政策 / 01",
  title: "结一隐私政策",
  updated: "最后更新：2026 年 8 月 26 日",
  summary:
    "本政策说明结一（Jieyi AI）在提供中文取名服务时收集哪些数据、为什么使用、与谁共享、保存多久，以及您如何查阅、删除或注销信息。",
  sections: [
    {
      title: "1. 数据控制者与联系方式",
      paragraphs: [
        "本服务由结一（Jieyi AI）服务运营方负责。隐私、数据访问、删除、支付争议或内容安全请求可通过本站“联系与举报”表单提交，我们会生成可追踪的举报编号。当前未设独立数据保护负责人。",
      ],
    },
    {
      title: "2. 我们实际收集的信息",
      bullets: [
        "账户信息：称呼、邮箱、密码哈希、会话、Google 登录授权的基本账户信息。我们不保存明文密码。",
        "中文取名信息：姓氏、性别倾向、字数、风格、典籍偏好、家庭寄语、必选/避用字，以及您自愿填写的出生日期、时间和地区。",
        "外国人中文名信息：原名、发音、母语、年龄范围、性格/职业描述、使用场景和用字偏好。",
        "交易信息：内部订单 ID、商品、金额、币种、支付状态和支付提供方订单 ID；完整卡号、微信支付凭据和钱包凭据不进入我们的服务器。",
        "技术与安全信息：IP 地址、浏览器/User-Agent、请求时间、错误信息、限流记录、审计和安全事件类别。",
        "联系与举报信息：举报邮箱、类别、相关 URL、说明和处理状态。",
      ],
    },
    {
      title: "3. 不收集的内容",
      paragraphs: [
        "结一当前仅生成中文姓名文本、解释和本地下载的分享卡，不提供面部、手部、真实人物照片或其他图像上传/生成能力。我们不要求身份证件、精确地址或生物识别数据。",
      ],
    },
    {
      title: "4. 使用目的与依据",
      bullets: [
        "履行服务合同：注册、创建订单、生成姓名、保存历史、收藏和返还失败次数。",
        "安全与合法利益：防止欺诈、滥用、重复扣款、攻击和违反 AIGC 使用规范的行为。",
        "法律义务：财务记录、争议处理、执法或监管机关的合法请求。",
        "用户同意：处理您主动选择提交的出生信息或 Google OAuth 授权信息。",
      ],
    },
    {
      title: "5. AI 数据最小化",
      paragraphs: [
        "服务使用 DeepSeek V4 Flash，必要时回退至 DeepSeek V4 Pro。中文八字参考先在服务端计算为四柱与五行摘要，精确出生日期、时间和地区不会发送给 DeepSeek。英文站会发送完成命名所需的原名、发音和个人描述。我们不使用您的输入训练自有模型。",
      ],
    },
    {
      title: "6. 共享与第三方处理方",
      bullets: [
        "Cloudflare：Workers、D1、Queues、静态资源、密码重置邮件、安全与运行日志。",
        "Waffo Pancake：托管收银台、支付、退款、税务和支付事件；并在生成前以无状态方式扫描最小化命名需求文本。精确出生日期、时间和地区不发送给该扫描服务，服务返回判定后不留存提示词原文。",
        "DeepSeek：仅处理完成命名所需的最小化输入和候选集合。",
        "Google：您选择 Google 登录时处理 OAuth 身份认证；您允许广告后，AdSense 可在未登录的公开页面展示广告并处理 Cookie、设备与广告交互信息。",
      ],
      paragraphs: [
        "我们不出售个人信息，也不把姓名、邮箱、取名内容或出生信息作为分析或广告事件参数发送。Google 分析与广告在您同意相应类别后加载；AdSense 不在账户、登录或付费结果页加载。Google 对广告数据的处理详见 https://policies.google.com/technologies/ads。",
      ],
    },
    {
      title: "7. Cookie、存储和跨境处理",
      paragraphs: [
        "我们使用严格必要的安全 Cookie 维持登录；仅在允许广告后加载 AdSense 广告 Cookie。您可从页脚隐私设置撤回选择。Cloudflare、Waffo Pancake、DeepSeek、Google 等供应商可能在您所在国家/地区之外处理数据；我们通过最小化传输、供应商协议、访问控制和适用的跨境保障措施降低风险。",
      ],
    },
    {
      title: "8. 安全措施",
      bullets: [
        "全站 HTTPS/TLS、HSTS、CSP、输入结构验证、访问权限和速率限制。",
        "密码哈希、Worker Secret、服务端固定价格、支付 Webhook 签名与订单级幂等。",
        "AI 输入与输出类别过滤、候选白名单和结构校验；安全日志不保存完整提示词。",
      ],
    },
    {
      title: "9. 保留期限",
      bullets: [
        "账户及取名记录：账户有效期间保留；您注销时删除个人取名记录并立即匿名化账户主记录。",
        "订单和必要财务记录：最长 7 年或适用法律要求的期限。",
        "已处理支付事件：最长 12 个月；审计日志：最长 24 个月。",
        "已结案举报：结案后最长 24 个月；限流记录：最多 7 天。",
        "本地加密/受控备份按照普通轮换处理；删除请求会在后续备份轮换中生效。",
      ],
    },
    {
      title: "10. 您的权利",
      paragraphs: [
        "您可以在账户页查看历史、订单和收藏，并直接注销账户。您也可以通过联系与举报表单请求访问、更正、删除、限制处理、数据副本、反对处理或撤回同意。我们通常在 30 个日历日内回复；依法需要保留的交易记录除外。",
      ],
    },
    {
      title: "11. 未成年人",
      paragraphs: [
        "本服务面向年满 18 周岁的用户。我们不会故意收集未成年人的个人信息；发现相关情况后将尽快限制处理并依法删除。",
      ],
    },
    {
      title: "12. 变更与投诉",
      paragraphs: [
        "重大变更将至少提前 15 天通过站内公告或可用的服务通知告知。若您认为我们未妥善处理个人信息，可通过联系与举报表单投诉，并保留向所在地主管机关投诉的权利。",
      ],
    },
  ],
};

const zhTerms: LegalDocument = {
  eyebrow: "服务条款 / 02",
  title: "结一服务条款",
  updated: "版本 1.0 · 生效日期：2026 年 8 月 26 日",
  summary:
    "创建账户、购买单次使用权或使用结一，即表示您已年满 18 周岁，并同意本条款、隐私政策、退款政策和 AIGC 使用规范。",
  sections: [
    {
      title: "1. 服务说明",
      paragraphs: [
        "结一是按次收费的数字中文取名 SaaS。中文站为 CNY 9.90/1 次或 CNY 89.00/10 次，英文站为 USD 4.99/1 次或 USD 39.99/10 次；每次成功交付包含 5 个姓名候选、解释和文化参考，不是订阅或自动续费服务。",
      ],
    },
    {
      title: "2. 底层 AI 与服务性质",
      paragraphs: [
        "本服务明确使用 DeepSeek V4 Flash 生成解释，在重试场景可能使用 DeepSeek V4 Pro，并结合结一自有规则、典籍候选、风险过滤和结构校验。结一不是 DeepSeek 官方产品，也未获得模型提供方背书。AI 输出为数字无形内容，可能受第三方模型和网络可用性影响。",
      ],
    },
    {
      title: "3. 账户与安全",
      bullets: [
        "注册信息必须准确，不得冒用他人邮箱或身份。",
        "您负责保护登录凭据，并对账户中的请求、购买和输出使用负责。",
        "发现未经授权的访问，请通过联系与举报表单提交“隐私/安全”类别。",
      ],
    },
    {
      title: "4. 单次使用权与付款",
      paragraphs: [
        "每笔购买均为一次性交易。提交支付即授权 Waffo Pancake 按收银台显示的商品、金额、币种和适用税费扣款。使用权不计利息、不可转售、不可兑换现金，在账户保持有效时不过期。重新生成会消耗新的使用权。",
      ],
    },
    {
      title: "5. 交付与失败返还",
      paragraphs: [
        "支付成功后，以经签名的支付事件增加站内使用次数。DeepSeek 超时、服务错误或结果未通过结构/安全校验时，系统自动返还该次站内使用权；返回支付成功页本身不代表次数已经入账。",
      ],
    },
    {
      title: "6. 退款",
      paragraphs: [
        "全部未使用的次数包可在付款后 7 日内申请审查；重复扣款、确认的计费错误或适用法律要求的情形可退款。已部分或全部交付姓名方案的数字内容通常不退款。详细条件、申请材料、处理时间和例外见独立退款政策。",
      ],
    },
    {
      title: "7. 用户输入、输出和知识产权",
      paragraphs: [
        "您保留合法输入和输出的权利。您授予结一有限、非独家许可，仅用于提供、保护和排查本次服务。您必须确保提交的姓名、文字、商标或其他材料拥有合法使用权。典籍引文、第三方商标和模型本身仍归各自权利人所有。",
      ],
    },
    {
      title: "8. 可接受使用",
      paragraphs: [
        "您必须遵守独立的 AIGC 使用规范。不得生成或请求色情/NSFW、暴力/血腥、仇恨、儿童不安全、未经同意的深度伪造/冒充、侵犯版权/商标内容，也不得攻击、爬取、转售、规避安全措施或批量滥用服务。",
      ],
    },
    {
      title: "9. 免责声明",
      paragraphs: [
        "姓名建议、评分、音律、典籍、生肖、五行或八字内容仅供文化与创意参考，不是户籍登记、法律、医疗、金融、教育或人生决策建议。AI 可能产生错误；您应自行核验字义、出处、读音、当地登记规则和现实语境，并自行决定是否使用。",
      ],
    },
    {
      title: "10. 违规与用户责任",
      paragraphs: [
        "违反条款时，我们可拒绝请求、移除内容、限制功能、暂停或永久关闭账户、撤销因欺诈获得的未使用次数、保留证据并依法报告。您应赔偿因违法输入、侵权使用、欺诈、拒付滥用或违反本条款给结一及第三方造成的合理损失。",
      ],
    },
    {
      title: "11. 责任限制",
      paragraphs: [
        "在法律允许范围内，服务按现状提供，我们不保证某个姓名一定可登记、无重名、符合所有文化偏好或产生特定结果。除故意或重大过失以及法律不得限制的责任外，我们对间接损失不承担责任；总责任不超过争议发生前 12 个月您向结一实际支付的金额。",
      ],
    },
    {
      title: "12. 终止、变更和争议",
      paragraphs: [
        "您可在账户页注销。我们可为安全、法律或重大产品调整暂停服务。重大条款变更至少提前 15 天公告。争议应先通过联系与举报表单协商；无法解决时，适用法律和有管辖权的法院规则继续适用，不影响消费者依法享有的强制性权利。",
      ],
    },
  ],
};

const zhRefund: LegalDocument = {
  eyebrow: "退款政策 / 03",
  title: "退款与失败返还政策",
  updated: "最后更新：2026 年 8 月 26 日",
  summary: "站内次数返还与原支付退款是两套机制；自动返还不等同于现金退款。",
  sections: [
    {
      title: "1. 自动返还站内次数",
      bullets: [
        "DeepSeek 超时或暂时不可用并最终超过重试次数。",
        "生成结果不足 5 个，或未通过结构、候选白名单、安全与出处校验。",
        "任务无法进入 Queue 或发生确认的内部服务错误。",
      ],
    },
    {
      title: "2. 可申请原路退款",
      bullets: [
        "付款后 7 日内且对应使用权尚未用于成功生成。",
        "重复扣款、金额/币种错误或订单未到账且无法补发。",
        "适用消费者保护法律明确要求退款的情形。",
      ],
    },
    {
      title: "3. 通常不退款",
      bullets: [
        "已经成功交付 5 个姓名方案的数字内容。",
        "因个人偏好改变、未采纳姓名或未先核验登记规则。",
        "因违反服务条款或 AIGC 使用规范而被限制或关闭的账户。",
      ],
    },
    {
      title: "4. 申请和处理",
      paragraphs: [
        "请通过联系与举报表单选择“账单/退款”，填写注册邮箱、订单日期、交易或订单 ID 和原因。我们通常在 2 个工作日内确认，5 个工作日内作出决定；批准后由 Waffo Pancake 原路处理，到账时间通常为 5–10 个工作日，具体取决于支付方式和金融机构。",
      ],
    },
  ],
};

const zhAup: LegalDocument = {
  eyebrow: "AIGC 使用规范 / 04",
  title: "可接受使用政策 — AI 生成内容",
  updated: "最后更新：2026 年 8 月 26 日",
  summary:
    "本规范适用于用户输入、姓名文本、AI 解释和下载分享卡。结一目前不提供面部、手部或其他真实人物图像生成，但以下标准同样适用于未来可能增加的图像功能。",
  sections: [
    {
      title: "1. 六类明确禁止内容",
      bullets: [
        "色情、裸体、露骨性内容或其他 NSFW 内容。",
        "写实暴力、虐待、肢解、血腥或鼓励现实伤害的内容。",
        "基于种族、民族、国籍、宗教、性别、性取向、残障等受保护特征的仇恨、贬损或煽动内容。",
        "任何儿童性剥削材料（CSAM）、儿童不安全内容或涉及未成年人的性化内容。",
        "未经本人明确同意的深度伪造、换脸、声音克隆、冒充真实个人、名人、品牌或机构。",
        "侵犯或诱导侵犯版权、商标、肖像、隐私、商业秘密等第三方权利的内容。",
      ],
    },
    {
      title: "2. 零容忍及其他禁止行为",
      bullets: [
        "对 CSAM、未成年人性剥削、性化或诱骗内容实行零容忍；无论是真实、虚构、合成还是以任何风格呈现，均拒绝生成并优先处置。",
        "恐怖主义、极端主义、大规模暴力或大规模杀伤性武器指导。",
        "欺诈、钓鱼、恶意软件、垃圾内容、虚假信息或规避安全措施。",
        "将 AI 输出冒充人工专业意见，或用于高风险医疗、法律、金融决定。",
        "批量爬取、转售、模型训练、知识蒸馏或绕过调用限制。",
      ],
    },
    {
      title: "3. 审核机制",
      bullets: [
        "生成前：输入先经过本地结构、长度和禁止类别过滤，再通过 Waffo Prompt Screening API 的 enforce 语义模式检查；只有 action=allow 才进入生成。",
        "生成中：模型只能从经过规则筛选的姓名候选集合中选择。",
        "展示前：输出经过 JSON 结构、候选白名单、重复、不宜字和六类内容复检。",
        "运行期：D1 限流、异常模式记录和不含完整提示词的类别审计日志。",
        "举报后：通过在线表单进入人工复核；当前没有公开作品广场。",
      ],
    },
    {
      title: "4. 举报和处理时效",
      paragraphs: [
        "任何人均可使用联系与举报表单提交内容安全、冒充、隐私或知识产权举报。儿童安全、恐怖主义等严重问题优先处理；通常在 24 小时内初步响应，其他举报在 3 个工作日内初步响应。请勿在说明中重复上传违法材料。",
      ],
    },
    {
      title: "5. 违规处置",
      bullets: [
        "拒绝生成、警告或移除相关内容。",
        "限制调用、暂停账户或对严重/重复违规永久封禁。",
        "保留必要证据，涉嫌违法或危及儿童安全时向主管机关报告。",
        "误判申诉可通过同一表单选择“其他”，附上原举报编号，通常在 10 个工作日内复核。",
      ],
    },
    {
      title: "6. AI 透明度",
      paragraphs: [
        "结一使用 DeepSeek V4 Flash，重试时可能回退至 DeepSeek V4 Pro。输出会明确作为 AI 辅助的文化建议展示。模型披露不代表 DeepSeek 对结一提供官方背书。",
      ],
    },
  ],
};

const zhAi: LegalDocument = {
  eyebrow: "AI 透明度 / 05",
  title: "底层 AI 模型与安全说明",
  updated: "最后更新：2026 年 8 月 26 日",
  summary: "结一如实披露实际调用的模型、规则系统、数据边界和输出限制。",
  sections: [
    {
      title: "实际使用的模型",
      bullets: [
        "主要生成模型：DeepSeek V4 Flash。",
        "失败重试/备用模型：DeepSeek V4 Pro。",
        "生成前安全服务：本地规则通过后、调用 DeepSeek 前，使用 Waffo Prompt Screening API 的 enforce 语义模式检查。",
        "本地规则：结一候选字库、典籍索引、声调/多音字/风险过滤，以及 tyme4ts 四柱五行摘要。",
      ],
    },
    {
      title: "当前不使用的模型类型",
      paragraphs: [
        "结一当前不提供人脸、手部、照片、音频或视频生成，因此未接入 Flux、GPT Image、Gemini Image、Veo、Kling 等图像/视频模型。若未来新增，会在功能上线前更新本页和 AIGC 使用规范。",
      ],
    },
    {
      title: "输出和安全边界",
      paragraphs: [
        "DeepSeek 仅负责在允许候选中选择并撰写解释；模型不能修改价格、余额、用户身份、订单状态或安全规则。所有输出在展示前经过结构、安全和候选白名单校验。结一是独立品牌，并非 DeepSeek 官方应用。",
      ],
    },
  ],
};

const enPrivacy: LegalDocument = {
  eyebrow: "PRIVACY / 01",
  title: "Jieyi Privacy Policy",
  updated: "Last updated: August 26, 2026",
  summary:
    "This policy explains what Jieyi AI collects, why we use it, where it goes, how long it is retained, and how you can access or delete it.",
  sections: [
    {
      title: "1. Controller and contact",
      paragraphs: [
        "The Jieyi AI service operator is responsible for this service. Submit privacy, access, deletion, billing, or safety requests through the Contact & Report form. It creates a trackable reference. No separate DPO is currently appointed.",
      ],
    },
    {
      title: "2. Data we actually collect",
      bullets: [
        "Account data: display name, email, password hash, sessions, and basic Google OAuth profile data when selected.",
        "Naming data: original name, pronunciation, language, age range, personality/work context, intended use, preferences, and generated results. The Chinese site may also collect an optional birth date, time, and region.",
        "Transaction data: internal order ID, product, amount, currency, status, and provider order IDs. Full card, WeChat, and wallet credentials never enter our servers.",
        "Security data: IP address, browser/User-Agent, request times, errors, rate limits, and non-content safety categories.",
        "Reports: reporter email, category, relevant URL, description, and handling status.",
      ],
    },
    {
      title: "3. Data we do not collect",
      paragraphs: [
        "Jieyi currently generates name text, explanations, and a locally downloaded share card. It does not offer face, hand, real-person image, audio, or video generation and does not ask for biometric data or identity documents.",
      ],
    },
    {
      title: "4. Purposes",
      bullets: [
        "Perform the contract: accounts, orders, names, history, favourites, and failed-credit restoration.",
        "Security and legitimate interests: prevent fraud, abuse, duplicate credits, attacks, and AIGC-policy violations.",
        "Legal obligations: accounting, disputes, and valid regulatory or law-enforcement requests.",
        "Consent: optional birth details and Google OAuth data you choose to provide.",
      ],
    },
    {
      title: "5. AI minimisation",
      paragraphs: [
        "We use DeepSeek V4 Flash and may fall back to DeepSeek V4 Pro. Exact Chinese-site birth date, time, and region are converted server-side into a four-pillar/element summary and are not sent to DeepSeek. The English site sends the original-name details needed for naming. We do not train our own model on your input.",
      ],
    },
    {
      title: "6. Processors and sharing",
      bullets: [
        "Cloudflare for Workers, D1, Queues, assets, password-reset emails, security, and operational logs.",
        "Waffo Pancake for hosted checkout, payment, refunds, tax, signed payment events, and a stateless pre-generation scan of minimised naming text. Exact birth date, time, and region are not sent to that scan, and prompt text is not retained after the verdict.",
        "DeepSeek for the minimised naming input and approved candidates.",
        "Google for optional OAuth sign-in; after advertising consent, AdSense may show ads on signed-out public pages and process cookies, device information, and ad interactions.",
      ],
      paragraphs: [
        "We do not sell personal information or send names, emails, naming input, or birth details as analytics or advertising event parameters. Tags load after the matching consent choice. AdSense does not load on account, sign-in, or paid result pages. See https://policies.google.com/technologies/ads for Google's advertising data practices.",
      ],
    },
    {
      title: "7. Cookies, international processing, and security",
      paragraphs: [
        "We use necessary secure cookies for authentication and load AdSense advertising cookies only after advertising consent. You can withdraw consent in the footer's Privacy settings. Providers may process data outside your country. Controls include TLS/HSTS/CSP, hashed passwords, least-privilege secrets, signed payment events, fixed server-side pricing, rate limits, structured validation, content filtering, and candidate allowlists.",
      ],
    },
    {
      title: "8. Retention",
      bullets: [
        "Account and naming data: while active; naming history is deleted and the user record immediately anonymised when you close the account.",
        "Orders and required financial records: up to 7 years or the period required by law.",
        "Processed payment events: up to 12 months; audit logs: up to 24 months.",
        "Closed reports: up to 24 months; rate-limit records: no more than 7 days.",
        "Controlled backups follow ordinary rotation; deletions propagate through later backup rotation.",
      ],
    },
    {
      title: "9. Your rights",
      paragraphs: [
        "View history, orders, and favourites or close your account from the account page. Use the Contact & Report form to request access, correction, deletion, restriction, portability, objection, or withdrawal of consent. We normally respond within 30 calendar days, subject to mandatory financial retention.",
      ],
    },
    {
      title: "10. Age, changes, and complaints",
      paragraphs: [
        "The service is for users aged 18 or older. Material changes are announced at least 15 days in advance. You may complain through the Contact & Report form or to your local data-protection authority.",
      ],
    },
  ],
};

const enTerms: LegalDocument = {
  eyebrow: "TERMS / 02",
  title: "Jieyi Terms of Service",
  updated: "Version 1.0 · Effective August 26, 2026",
  summary:
    "By creating an account, purchasing a generation, or using Jieyi, you confirm that you are 18 or older and accept these Terms, the Privacy Policy, Refund Policy, and AIGC Policy.",
  sections: [
    {
      title: "1. Service",
      paragraphs: [
        "Jieyi is a pay-per-use Chinese naming SaaS. The Chinese site costs CNY 9.90 for one generation or CNY 89.00 for ten; the English site costs USD 4.99 for one or USD 39.99 for ten. Each generation delivers five name candidates and explanations. There is no subscription or automatic renewal.",
      ],
    },
    {
      title: "2. Underlying AI",
      paragraphs: [
        "Jieyi specifically uses DeepSeek V4 Flash and may use DeepSeek V4 Pro on retry, together with proprietary rules, literary candidates, risk filters, and schema checks. Jieyi is independent and is not an official DeepSeek product or endorsed by the model provider.",
      ],
    },
    {
      title: "3. Accounts",
      bullets: [
        "Provide accurate information and do not impersonate another person.",
        "Protect your credentials and accept responsibility for activity under your account.",
        "Report unauthorised access through Contact & Report using the privacy/security category.",
      ],
    },
    {
      title: "4. One-time credits and payment",
      paragraphs: [
        "Each purchase is a one-time charge. You authorise Waffo Pancake to charge the product, amount, currency, and applicable taxes shown at checkout. Credits bear no interest, cannot be resold or redeemed for cash, and do not expire while the account remains active. A regeneration consumes another credit.",
      ],
    },
    {
      title: "5. Delivery, failures, and refunds",
      paragraphs: [
        "A signed payment event grants the credit. Final AI timeout, service error, or failed structure/safety validation restores the site credit. Unused purchases may be reviewed within seven days; duplicate charges, confirmed billing errors, and mandatory consumer rights remain refundable. Successfully delivered digital generations are normally non-refundable. See the Refund Policy.",
      ],
    },
    {
      title: "6. Inputs, outputs, and rights",
      paragraphs: [
        "You retain rights in lawful inputs and outputs and grant Jieyi a limited, non-exclusive licence only to provide, secure, and troubleshoot the service. You must have the right to submit names, text, marks, or other material. Literary quotations, third-party marks, and AI models remain with their owners.",
      ],
    },
    {
      title: "7. Acceptable use",
      paragraphs: [
        "You must follow the AIGC Policy. Sexual/NSFW, graphic violence, hate, child-unsafe content, non-consensual deepfakes/impersonation, and copyright/trademark infringement are expressly prohibited, as are attacks, scraping, resale, safety bypass, and bulk abuse.",
      ],
    },
    {
      title: "8. Disclaimer",
      paragraphs: [
        "Names, scores, sound, literature, zodiac, elements, and birth-time references are cultural and creative material, not identity-registration, legal, medical, financial, or professional advice. AI may be wrong. You must verify meanings, sources, pronunciation, registration rules, and real-world context before use.",
      ],
    },
    {
      title: "9. Breach and user responsibility",
      paragraphs: [
        "We may reject a request, remove content, limit features, suspend or terminate an account, revoke unused fraudulently obtained credits, preserve evidence, and report suspected illegality. You are responsible for reasonable losses caused by unlawful input, infringement, fraud, abusive chargebacks, or breach of these Terms.",
      ],
    },
    {
      title: "10. Liability",
      paragraphs: [
        "To the extent permitted by law, the service is provided as-is. We do not promise that a name is registrable, unique, universally appropriate, or produces a particular outcome. Except for wilful misconduct, gross negligence, or liability that cannot be limited, indirect losses are excluded and aggregate liability is capped at fees you paid in the preceding 12 months.",
      ],
    },
    {
      title: "11. Termination, changes, and disputes",
      paragraphs: [
        "Close your account from the account page. We may suspend for safety, law, or major product changes. Material Terms changes receive at least 15 days' notice. Submit disputes through Contact & Report first; mandatory consumer law and competent-court rules continue to apply.",
      ],
    },
  ],
};

const enRefund: LegalDocument = {
  eyebrow: "REFUNDS / 03",
  title: "Refund and Credit-Restoration Policy",
  updated: "Last updated: August 26, 2026",
  summary: "Site-credit restoration and a cash refund are different processes.",
  sections: [
    {
      title: "1. Automatic credit restoration",
      bullets: [
        "DeepSeek times out or remains unavailable after final retry.",
        "Fewer than five results are available or output fails schema, allowlist, safety, or source checks.",
        "The job cannot be queued or a confirmed internal service error occurs.",
      ],
    },
    {
      title: "2. Refundable cases",
      bullets: [
        "Within seven days of payment where the credit has not produced a successful generation.",
        "Duplicate charge, wrong amount/currency, or an order that cannot be delivered or repaired.",
        "A refund required by applicable consumer law.",
      ],
    },
    {
      title: "3. Normally non-refundable",
      bullets: [
        "A successfully delivered five-name digital generation.",
        "A change of preference, a decision not to use a name, or failure to verify local registration rules.",
        "An account restricted for violating the Terms or AIGC Policy.",
      ],
    },
    {
      title: "4. Request and timing",
      paragraphs: [
        "Use Contact & Report, choose Billing/Refund, and provide the account email, payment date, transaction/order ID, and reason. We normally acknowledge within two business days and decide within five. Approved refunds are returned by Waffo Pancake; arrival is normally 5–10 business days depending on the payment method and institution.",
      ],
    },
  ],
};

const enAup: LegalDocument = {
  eyebrow: "AIGC POLICY / 04",
  title: "Acceptable Use Policy — AI-Generated Content",
  updated: "Last updated: August 26, 2026",
  summary:
    "This policy covers user input, generated name text, explanations, and downloaded share cards. Jieyi currently has no face, hand, or real-person image generator; the same rules apply to any future image feature.",
  sections: [
    {
      title: "1. Six expressly prohibited categories",
      bullets: [
        "Sexual, nude, explicit, pornographic, or other NSFW content.",
        "Graphic violence, gore, torture, dismemberment, or encouragement of real-world harm.",
        "Hate, degradation, or incitement based on protected characteristics.",
        "CSAM, child-unsafe content, or any sexualisation of a minor.",
        "Non-consensual deepfakes, face swaps, voice clones, or impersonation of a person, brand, or institution.",
        "Copyright, trademark, likeness, privacy, trade-secret, or other intellectual-property infringement.",
      ],
    },
    {
      title: "2. Zero-tolerance and additional prohibitions",
      bullets: [
        "We apply zero tolerance to CSAM, child sexual exploitation, sexualisation, or grooming content, whether real, fictional, synthetic, or presented in any style; such requests are refused and prioritised for enforcement.",
        "Terrorism, extremism, mass violence, or weapons-of-mass-destruction instructions.",
        "Fraud, phishing, malware, spam, disinformation, or bypass of safeguards.",
        "Presenting AI output as human professional medical, legal, or financial advice.",
        "Bulk scraping, resale, model training/distillation, or bypassing usage limits.",
      ],
    },
    {
      title: "3. Moderation",
      bullets: [
        "Before generation: local schema, length, and prohibited-category filters, followed by Waffo Prompt Screening API in enforce semantic mode; generation proceeds only for action=allow.",
        "During generation: the model may select only from rule-approved name candidates.",
        "Before display: JSON schema, allowlist, duplicate, unsuitable-character, and category re-checks.",
        "During operation: D1 rate limits, anomaly records, and category-only audit logs without full prompts.",
        "After a report: human review through the online form. There is no public gallery today.",
      ],
    },
    {
      title: "4. Reports, enforcement, and appeals",
      paragraphs: [
        "Submit safety, impersonation, privacy, or IP concerns through Contact & Report. Child-safety and terrorism matters are prioritised and normally receive an initial response within 24 hours; other reports normally receive an initial response within three business days. Do not re-upload illegal material in the report.",
      ],
      bullets: [
        "Actions may include refusal, warning, removal, feature limits, suspension, permanent termination, evidence preservation, and lawful reporting.",
        "Appeal a suspected mistake through the same form under Other and include the original report ID; review normally completes within ten business days.",
      ],
    },
    {
      title: "5. AI transparency",
      paragraphs: [
        "Jieyi uses DeepSeek V4 Flash and may fall back to DeepSeek V4 Pro. Outputs are labelled as AI-assisted cultural suggestions. Model disclosure does not imply endorsement by DeepSeek.",
      ],
    },
  ],
};

const enAi: LegalDocument = {
  eyebrow: "AI TRANSPARENCY / 05",
  title: "Underlying AI Models and Safety",
  updated: "Last updated: August 26, 2026",
  summary: "The exact models, local rules, data boundary, and limitations used by Jieyi.",
  sections: [
    {
      title: "Models in use",
      bullets: [
        "Primary generation model: DeepSeek V4 Flash.",
        "Retry/fallback model: DeepSeek V4 Pro.",
        "Pre-generation safety service: Waffo Prompt Screening API in enforce semantic mode, after local rules and before DeepSeek.",
        "Local systems: Jieyi candidates and literary sources, pronunciation/polyphonic/risk filters, and tyme4ts four-pillar/element summaries.",
      ],
    },
    {
      title: "Models not currently used",
      paragraphs: [
        "Jieyi has no face, hand, photo, audio, or video generation. It therefore does not currently integrate Flux, GPT Image, Gemini Image, Veo, Kling, or another image/video model. This page and the AIGC Policy will be updated before such a feature launches.",
      ],
    },
    {
      title: "Boundaries",
      paragraphs: [
        "DeepSeek selects and explains approved candidates. It cannot alter prices, balances, identity, orders, or safety rules. Every output is checked before display. Jieyi is an independent brand and not an official DeepSeek application.",
      ],
    },
  ],
};

const zhCookies: LegalDocument = {
  eyebrow: "COOKIE 政策 / 06",
  title: "Cookie 与分析偏好",
  updated: "最后更新：2026 年 9 月 7 日",
  summary: "必要存储始终用于登录与安全；分析和广告标签只在您明确同意后启用。",
  sections: [
    {
      title: "必要存储",
      paragraphs: [
        "登录会话、安全、防欺诈和语言偏好属于服务运行所必需。关闭这些存储可能导致账户功能不可用。",
      ],
    },
    {
      title: "分析与广告",
      paragraphs: [
        "AdSense 发布商为 ca-pub-1943097022556556；仅在您选择“全部允许”后，才可能在未登录公开页加载广告。选择“允许分析”不会加载 AdSense。Google Analytics 与 Tag Manager 另行配置，配置为空时不加载。",
        "分析事件不会包含姓名、邮箱、取名寄语、出生信息或生成结果。营销同意后才会保存 GCLID、GBRAID、WBRAID 等广告归因参数。",
      ],
    },
    {
      title: "修改选择",
      paragraphs: ["您可随时通过页脚“隐私设置”重新选择；撤回不会影响此前合法完成的处理。"],
    },
  ],
};

const enCookies: LegalDocument = {
  eyebrow: "COOKIE POLICY / 06",
  title: "Cookies and Analytics Preferences",
  updated: "Last updated: September 7, 2026",
  summary:
    "Essential storage supports sign-in and security. Analytics and advertising tags only load after explicit consent.",
  sections: [
    {
      title: "Essential storage",
      paragraphs: [
        "Sign-in sessions, security, fraud prevention, and language preferences are necessary for the service. Disabling them may prevent account features from working.",
      ],
    },
    {
      title: "Analytics and advertising",
      paragraphs: [
        "AdSense publisher ca-pub-1943097022556556 may load ads on signed-out public pages only after you choose Allow all. Allow analytics does not enable AdSense. Analytics and Tag Manager are configured separately and do not load when their configuration is empty.",
        "Events never contain names, email addresses, naming wishes, birth details, or generated results. Advertising click identifiers are stored only with marketing consent.",
      ],
    },
    {
      title: "Changing your choice",
      paragraphs: [
        "You can reopen Privacy Settings from the footer at any time. Withdrawal does not affect processing lawfully completed before it.",
      ],
    },
  ],
};

const DOCUMENTS: Record<LegalLanguage, Record<LegalPath, LegalDocument>> = {
  zh: {
    "/privacy": zhPrivacy,
    "/terms": zhTerms,
    "/refund": zhRefund,
    "/acceptable-use": zhAup,
    "/ai-disclosure": zhAi,
    "/cookies": zhCookies,
  },
  en: {
    "/privacy": enPrivacy,
    "/terms": enTerms,
    "/refund": enRefund,
    "/acceptable-use": enAup,
    "/ai-disclosure": enAi,
    "/cookies": enCookies,
  },
};

export const LEGAL_PATHS = new Set<LegalPath>([
  "/privacy",
  "/terms",
  "/refund",
  "/acceptable-use",
  "/ai-disclosure",
  "/cookies",
]);

export function getLegalDocument(language: LegalLanguage, path: LegalPath): LegalDocument {
  return DOCUMENTS[language][path];
}

export function LegalDocumentContent({ document }: { document: LegalDocument }) {
  return (
    <>
      <span className="policy-eyebrow">{document.eyebrow}</span>
      <h1>{document.title}</h1>
      <p className="policy-updated">{document.updated}</p>
      <p className="policy-summary">{document.summary}</p>
      <nav className="policy-nav" aria-label="Policy navigation">
        <a href="/terms">Terms / 条款</a>
        <a href="/privacy">Privacy / 隐私</a>
        <a href="/refund">Refunds / 退款</a>
        <a href="/acceptable-use">AIGC Policy / 使用规范</a>
        <a href="/ai-disclosure">AI Models / 模型披露</a>
        <a href="/cookies">Cookies / Cookie</a>
        <a href="/report">Contact & Report / 联系举报</a>
      </nav>
      <div className="policy-sections">
        {document.sections.map((section) => (
          <section className="policy-section" key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.bullets && (
              <ul>
                {section.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
      <p className="policy-legal-note">
        This policy is operational information, not legal advice. /
        本政策为运营说明，不构成法律意见。
      </p>
    </>
  );
}
